import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-ducens-ink text-white hover:bg-black focus-visible:ring-ducens-ink',
  secondary:
    'bg-ducens-accent text-ducens-ink hover:bg-ducens-accent-hover focus-visible:ring-ducens-accent',
  ghost:
    'bg-transparent text-ducens-ink hover:bg-ducens-bg focus-visible:ring-ducens-ink',
  outline:
    'bg-white text-ducens-ink border border-ducens-border hover:border-ducens-ink focus-visible:ring-ducens-ink',
};

const SIZE: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

const BASE =
  'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

type CommonProps = {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
};

type ButtonAsButton = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    as?: 'button';
  };

type ButtonAsLink = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    as: 'link';
    to: string;
    external?: boolean;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (props, ref) => {
    const variant = props.variant ?? 'primary';
    const size = props.size ?? 'md';
    const classes = cn(BASE, VARIANT[variant], SIZE[size], props.className);

    if (props.as === 'link') {
      const { as: _as, to, external, variant: _v, size: _s, className: _c, children, ...rest } = props;
      void _as; void _v; void _s; void _c;
      if (external) {
        return (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={to}
            className={classes}
            target="_blank"
            rel="noreferrer noopener"
            {...rest}
          >
            {children}
          </a>
        );
      }
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={classes}
          {...rest}
        >
          {children}
        </Link>
      );
    }

    const { as: _as, variant: _v, size: _s, className: _c, children, ...rest } = props;
    void _as; void _v; void _s; void _c;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        {...rest}
      >
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
